/**
 * CommunityGuidelinesModal — read-and-acknowledge for an ORG's own
 * guidelines, inside the Action Center task (Skip, 2026-07-08).
 *
 * Third sibling of EulaModal / CommunityStandardsModal: same chrome
 * (shared CSS module), same proof-of-read scroll gate. Content differs —
 * it is the org-authored text from `community_guidelines` (DB), passed
 * in as a prop, not bundled markdown.
 *
 * §7.9 note: the checklist row remains a POINTER and the org About page
 * remains the durable home of the guidelines — this modal displays the
 * same content in-task so the acknowledgment is read-to-accept (no bare
 * acknowledge that skips the reading), mirroring the platform-standards
 * act. Flagged as a §7.9 wording amendment (pointer row + in-task
 * content view + durable About home).
 *
 * The accept ACTION is injected (`onAccept`) because the two consumers
 * acknowledge differently (invited-org hook vs. per-org API call).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { renderMarkdown } from '../EulaModal/EulaModal';
import styles from '../EulaModal/EulaModal.module.css';
import { sanitizeRichText } from '../../utils/sanitizeHtml';

export interface CommunityGuidelinesModalProps {
  open: boolean;
  /** Org display name — the modal title is "{org} guidelines". */
  orgName: string;
  /** Guidelines text (markdown-ish, org-authored, from the DB). */
  content: string;
  /** Content version (bumped on edit) — shown as the version label. */
  version: number | null;
  /** Perform the acknowledgment (host-specific). Reject to show error. */
  onAccept: () => Promise<void>;
  /** Close without acknowledging (always allowed — the row stays pending). */
  onClose: () => void;
}

const CommunityGuidelinesModal: React.FC<CommunityGuidelinesModalProps> = ({
  open,
  orgName,
  content,
  version,
  onAccept,
  onClose,
}) => {
  const { t } = useAppTranslation('pages');
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [scrolledEnough, setScrolledEnough] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bodyHtml = React.useMemo(() => renderMarkdown(content || ''), [content]);

  const handleScroll = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    const distance = el.scrollTop + el.clientHeight;
    const total = el.scrollHeight;
    if (total <= el.clientHeight) {
      setScrolledEnough(true);
      return;
    }
    if (distance / total >= 0.8) {
      setScrolledEnough(true);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setScrolledEnough(false);
      setError(null);
      setSubmitting(false);
      requestAnimationFrame(handleScroll);
    }
  }, [open, handleScroll]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const handleAccept = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onAccept();
      setSubmitting(false);
    } catch (err: any) {
      setError(err?.message || t('guidelinesModal.errorGeneric'));
      setSubmitting(false);
    }
  }, [onAccept, t]);

  if (!open) return null;

  return (
    <div className={styles.backdrop} role="presentation">
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="guidelines-modal-title"
      >
        <div className={styles.header}>
          <div>
            <h1 id="guidelines-modal-title" className={styles.title}>
              {t('guidelinesModal.title', { org: orgName })}
            </h1>
            {version != null && (
              <span className={styles.versionLabel}>
                {t('guidelinesModal.version', { version })}
              </span>
            )}
          </div>
          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={onClose}
              aria-label={t('guidelinesModal.close')}
            >
              ×
            </button>
          </div>
        </div>

        {/* Body — org-authored content; sanitized at the render boundary
            (sanitizeRichText — one policy for every innerHTML sink). */}
        <div
          ref={bodyRef}
          className={styles.body}
          onScroll={handleScroll}
          dangerouslySetInnerHTML={{ __html: sanitizeRichText(bodyHtml) }}
        />

        <div className={styles.footer}>
          {error && (
            <div role="alert" className={styles.errorMessage}>
              {error}
            </div>
          )}
          {!scrolledEnough && (
            <div className={styles.scrollHint}>{t('guidelinesModal.scrollHint')}</div>
          )}
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={onClose}
              disabled={submitting}
            >
              {t('guidelinesModal.cancel')}
            </button>
            <button
              type="button"
              className={styles.acceptBtn}
              onClick={handleAccept}
              disabled={!scrolledEnough || submitting}
            >
              {submitting
                ? t('guidelinesModal.submitting')
                : t('guidelinesModal.accept')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityGuidelinesModal;
